import { Formik } from 'formik';
import shuffle from 'lodash/shuffle';
import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';

import DropdownField from '@components/Form/DropdownField';
import TextAreaField from '@components/Form/TextAreaField';
import { useProfileUpdate } from '@hooks';
import { SOURCE_OPTIONS } from '@utils/backend-api/profileSettings';
import { BLANK_OPTION } from '@utils/constants';

import StepContainer from '../StepContainer';
import schema from './schema';

interface StepReferralSourceProps {
  currentUserId: number;
  CurrentReferralSource?: string;
  nextStep: () => void;
  skipEvent: () => void;
  continueEvent: ({ payload }: { payload: object }) => void;
}

const INITIAL_VALUES = {
  customReferralSource: '',
  referralSource: BLANK_OPTION,
};

const OTHER = 'other';

const StepReferralSource = ({
  CurrentReferralSource,
  currentUserId,
  nextStep,
  skipEvent,
  continueEvent,
}: StepReferralSourceProps) => {
  const [initialValues, setInitialValues] = useState(INITIAL_VALUES);
  const { saveProfile, error } = useProfileUpdate({ userId: currentUserId });
  const filteredOptionsWithoutOther = SOURCE_OPTIONS.filter((option) => option.value !== OTHER);
  const StandardshuffledOptions = useMemo(() => {
    const shuffledOptions = shuffle(filteredOptionsWithoutOther);
    shuffledOptions.push(SOURCE_OPTIONS.find((option) => option.value === OTHER));
    return shuffledOptions;
  }, [filteredOptionsWithoutOther]);

  const handleSubmit = async (values) => {
    const sanitizedParams = {
      referral_source:
        values.referralSource.value === OTHER
          ? values.customReferralSource
          : values.referralSource.value,
    };
    saveProfile(sanitizedParams);
    continueEvent({ payload: sanitizedParams });
    nextStep();
  };

  const getSelectedOptions = (value) => {
    const standardOption = SOURCE_OPTIONS?.find((item) => item.value === value) || BLANK_OPTION;
    const customOption = '';
    if (value && !standardOption.label) {
      return {
        standardOption: { label: 'Other', value: OTHER },
        customOption: value,
      };
    }
    return { standardOption, customOption };
  };

  useEffect(() => {
    if (CurrentReferralSource) {
      const referralSourceStandardAndCustomOptions = getSelectedOptions(CurrentReferralSource);
      setInitialValues({
        referralSource: referralSourceStandardAndCustomOptions.standardOption,
        customReferralSource: referralSourceStandardAndCustomOptions.customOption,
      });
    }
  }, [CurrentReferralSource]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleSubmit}
    >
      {(formik) => {
        return (
          <StepContainer
            heading="Where did you hear about The Information?"
            headerClassName="text-headline-xxs"
            onContinue={formik.handleSubmit}
            nextStep={nextStep}
            error={error}
            skipEvent={skipEvent}
          >
            <Form onSubmit={formik.handleSubmit}>
              <DropdownField
                name="referralSource"
                options={StandardshuffledOptions}
                placeholder="Select"
                isClearable={false}
                className="mb-32"
                scrollIntoView
              />
              {formik.values.referralSource.value === OTHER && (
                <div className="mt-4">
                  <TextAreaField
                    name="customReferralSource"
                    placeholder="If other enter where"
                    rows={3}
                  />
                </div>
              )}
            </Form>
          </StepContainer>
        );
      }}
    </Formik>
  );
};

export default StepReferralSource;
